import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessageInternal, NoddiSheet, NoddiSkeleton, useAppForm } from 'noddi-ui-web';
import { getUserDisplayName } from 'noddi-util';
import { useEffect } from 'react';
import { z } from 'zod';
import { TanstackServiceWorkersAsyncMultiSelect } from '../../form/tanstack/features/Account/TanstackServiceWorkersAsyncMultiSelect';

const FORM_ID = 'edit-customer-dedicated-workers-form';

const validationSchema = z.object({
  serviceWorkers: z.array(z.object({ value: z.number(), label: z.string() }))
});

type FormValues = z.input<typeof validationSchema>;

/** Dedicated workers are set per service organization. */
export type DedicatedWorkersScope = {
  serviceOrganizationId: number;
  /** Limits the worker picker to these service departments (the partner's selection). */
  serviceDepartmentIds: number[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  userGroupId: number;
  scope: DedicatedWorkersScope;
};

export const DedicatedWorkersSheet = ({ open, onClose, userGroupId, scope }: Props) => {
  const { serviceOrganizationId, serviceDepartmentIds } = scope;
  const input = { userGroupId, serviceOrganizationId };

  const { data, isPending, error } = noddiAsync.useGet({ type: URLKeys.getDedicatedServiceWorkers, input });

  const { mutateAsync: patchDedicatedServiceWorkers } = noddiAsync.usePatch({
    type: URLKeys.patchDedicatedServiceWorkers,
    queryConfig: {
      onSuccess: () => invalidateQueryExactMatch({ urlKey: URLKeys.getDedicatedServiceWorkers, input })
    }
  });

  const getDefaultValues = (): FormValues => ({
    serviceWorkers: (data?.serviceWorkers ?? []).map((worker) => ({
      value: worker.id,
      label: getUserDisplayName(worker)
    }))
  });

  const form = useAppForm({
    defaultValues: getDefaultValues(),
    validators: { onSubmit: validationSchema },
    onSubmit: async ({ value }) => {
      await patchDedicatedServiceWorkers(
        { ...input, serviceWorkerIds: value.serviceWorkers.map((worker) => worker.value) },
        { onSuccess: onClose }
      );
    }
  });

  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form is a stable instance and getDefaultValues is recreated each render; the saved workers are what it reads, so the form reseeds only when those change
  }, [open, data]);

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            // The worker picker seeds its selection once on mount, so a discarded edit must not reach the next open.
            form.reset(getDefaultValues());
            onClose();
          }
        }}
        title='Dedicated workers'
        description='When one of them has a shift, route planning gives this customer’s bookings only to them. When none has, any worker can take them.'
        footer={<form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />}
      >
        {error && <ApiErrorMessageInternal error={error} />}
        {isPending ? (
          <NoddiSkeleton className='h-10 w-full' />
        ) : (
          <form
            id={FORM_ID}
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.AppField name='serviceWorkers'>
              {() => (
                <TanstackServiceWorkersAsyncMultiSelect
                  label='Workers'
                  tooltip='Leave empty to let any worker serve this customer.'
                  filters={{ serviceDepartmentIds }}
                />
              )}
            </form.AppField>
          </form>
        )}
      </NoddiSheet>
    </form.AppForm>
  );
};
